﻿<script lang="ts">
    import { f7 } from 'framework7-svelte';
    import type { Calendar } from 'framework7/framework7-types';
    import { createEventDispatcher } from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import {datesAreEqual, subtractDays, toDateOnly} from "../helpers/date.helper";
 
    export let enabledTimestamps: Date[] = [];
    export let range: boolean = false;
    export let selectedTimestamp: Date;
    export let resetDates: boolean = false;

    let selectedDateRange: Date[] = [];
    
    const dispatch = createEventDispatcher<{ selected: Date | Date[] }>();

    let calendar: Calendar.Calendar;
    let calendarContainer: HTMLElement;

    $: distinctDates = mapDistinctDates(enabledTimestamps);

    let initialized = false;

    $: if(resetDates) {
        selectedDateRange = [];
        calendar.setValue([]);
        
        resetDates = false;
    }
    
    $: if (calendarContainer && !initialized && distinctDates.length > 0) {
        initialized = true;
        initializeCalendar();
    }
    
    $: if ((selectedTimestamp || selectedDateRange) && calendar) {
        const calendarValue = calendar.params.value?.[0]
        if(!range && (calendarValue || !datesAreEqual(calendarValue, selectedTimestamp))) {
            calendar.setValue([selectedTimestamp]);
        }
        else if(range && (calendarValue || selectedDateRange.length > 0)) {
            calendar.setValue(selectedDateRange);
            calendar.setYearMonth(selectedDateRange[0].getFullYear(), selectedDateRange[0].getMonth(), 0);
        }
        else {
            calendar.setValue([]);
        }
        
        calendar.update();
    }
    
    function mapDistinctDates(timestampsInput: Date[]): Date[] {
        const distinctDates = new Map<string, Date>();
        const datesOnly: Date[] = timestampsInput.map(toDateOnly);

        datesOnly.forEach((date) => {
            const dateString = date.toDateString();
            if (!distinctDates.has(dateString)) {
                distinctDates.set(dateString, date);
            }
        });
        
        return Array.from(distinctDates.values());
    }

    function initializeCalendar() {
        const oldestDate = new Date(distinctDates[0]);
        const mostRecentDate = new Date(distinctDates[distinctDates.length - 1]);
        subtractDays(oldestDate, 1);
        
        if(!calendar){
            calendar = createCalendar(oldestDate, mostRecentDate, extractDisabledDates(oldestDate, mostRecentDate));
            calendar.setYearMonth(mostRecentDate?.getFullYear(), mostRecentDate?.getMonth(), 0);
        } 
        else if(!datesAreEqual(calendar.params.maxDate, mostRecentDate)) {
            calendar.params.minDate = oldestDate;
            calendar.params.maxDate = mostRecentDate;
            calendar.params.disabled = extractDisabledDates(oldestDate, mostRecentDate);
            calendar.update();
        }
    }

    function createCalendar(startDate: Date, endDate: Date, disabledDates: Calendar.DateRange): Calendar.Calendar {
        return f7.calendar.create({
            containerEl: calendarContainer,
            minDate: startDate,
            maxDate: endDate,
            disabled: disabledDates,
            value: range ? [] : [endDate],
            rangePicker: !!range,
            dayNames: [
                $_('shared.SUNDAY'),
                $_('shared.MONDAY'),
                $_('shared.TUESDAY'),
                $_('shared.WEDNESDAY'),
                $_('shared.THURSDAY'),
                $_('shared.FRIDAY'),
                $_('shared.SATURDAY')
            ],
            dayNamesShort: [
                $_('shared.SUNDAY_SHORT'),
                $_('shared.MONDAY_SHORT'),
                $_('shared.TUESDAY_SHORT'),
                $_('shared.WEDNESDAY_SHORT'),
                $_('shared.THURSDAY_SHORT'),
                $_('shared.FRIDAY_SHORT'),
                $_('shared.SATURDAY_SHORT')
            ],
            touchMove: false,
            renderToolbar: toolbarRenderer,
            on: {
                init: setToolbarTextAndListeners,
                monthYearChangeStart: formatDatePickerToolbarText,
                dayClick: onDayClick,
                change: onChange
            }
        });
    }
    
    function toolbarRenderer() {
        const showPrev = true; // @TODO: remove button when start date is in first shown month
        const showNext = true; // @TODO: remove button when end date is in last shown month

        return `
            <div class="toolbar calendar-custom-toolbar no-shadow">
                <div class="toolbar-inner">
                    <div class="left">
                        ${
                            showPrev
                                ? `
                                <a class="link icon-only">
                                    <i class="icon icon-back"></i>
                                </a>`
                                : 'abc'
                        }
                    </div>
                    <div class="center"></div>
                    <div class="right">
                    ${
                        showNext
                            ? `
                            <a  class="link icon-only">
                                <i class="icon icon-forward"></i>
                            </a>`
                            : 'def'
                    }
                    </div>
                </div>
            </div>
        `;
    }
    
    function setToolbarTextAndListeners(calendar: Calendar.Calendar) {
        formatDatePickerToolbarText(calendar);
        f7.$('.calendar-custom-toolbar .left .link').on('click', function () {
            calendar.prevMonth(1);
        });
        f7.$('.calendar-custom-toolbar .right .link').on('click', function () {
            calendar.nextMonth(1);
        });
    }
    
    function formatDatePickerToolbarText(calendar: Calendar.Calendar) {
        const monthNames = [
            $_('shared.JANUARY'),
            $_('shared.FEBRUARY'),
            $_('shared.MARCH'),
            $_('shared.APRIL'),
            $_('shared.MAY'),
            $_('shared.JUNE'),
            $_('shared.JULY'),
            $_('shared.AUGUST'),
            $_('shared.SEPTEMBER'),
            $_('shared.OCTOBER'),
            $_('shared.NOVEMBER'),
            $_('shared.DECEMBER')
        ];
        
        f7.$('.calendar-custom-toolbar .center').text(monthNames[calendar.currentMonth] + ', ' + calendar.currentYear);
    }
    
    function onDayClick(calendar: Calendar.Calendar, dayElement: HTMLElement, year: number, month: number, day: number) {
        if (range) return;
        
        const pickedDate = new Date(year, month, day);
        dispatch('selected', pickedDate);
    }
    
    function onChange(calendar: Calendar.Calendar, value: any) {
        if (!range || !value) return;

        selectedDateRange = value;
        dispatch('selected', value);
    }

    function extractDisabledDates(startDate: Date, endDate: Date): Calendar.DateRange {
        const disabledDates = [];
        const enabledDateStrings = new Set(distinctDates.map((date) => date.toDateString()));

        for (let dateIterator = new Date(startDate); dateIterator <= endDate; dateIterator.setDate(dateIterator.getDate() + 1)) {
            if (!enabledDateStrings.has(dateIterator.toDateString())) {
                disabledDates.push({ date: new Date(dateIterator) });
            }
        }
        
        return disabledDates;
    }
</script>

<div bind:this={calendarContainer}></div>
